import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Layout from "../../components/layout/Layout";
import adminUsersApi, { AdminUserPayload } from "../../api/adminUsers";
import kitchensApi from "../../api/kitchens";
import {
  Admin,
  ADMIN_PERMISSION_KEYS,
  ADMIN_ROLE_OPTIONS,
  Kitchen,
} from "../../types/auth";

const permissionLabels: Record<string, string> = {
  analytics: "Dashboard & Analytics",
  orders: "Orders",
  subscriptions: "Subscriptions",
  users: "Customers",
  deliveryAgents: "Delivery Agents",
  discounts: "Discounts",
  meals: "Meals",
  nowMeals: "Instant Meals",
  swapSets: "Swap Sets",
  kitchens: "Kitchens",
  holidays: "Holidays",
  admins: "User Management",
  instantOrders: "Instant Orders",
};

const buildDefaultPermissions = (role: string) => {
  switch (role) {
    case "super-admin":
      return {
        analytics: true,
        orders: true,
        subscriptions: true,
        users: true,
        deliveryAgents: true,
        discounts: true,
        meals: true,
        nowMeals: true,
        swapSets: true,
        kitchens: true,
        holidays: true,
        admins: true,
        instantOrders: true,
      };
    case "admin":
      return {
        analytics: true,
        orders: true,
        subscriptions: true,
        users: true,
        deliveryAgents: true,
        discounts: true,
        meals: true,
        nowMeals: true,
        swapSets: true,
        kitchens: true,
        holidays: true,
        admins: false,
        instantOrders: true,
      };
    case "manager":
      return {
        analytics: true,
        orders: true,
        subscriptions: true,
        users: true,
        deliveryAgents: true,
        discounts: true,
        meals: true,
        nowMeals: true,
        swapSets: true,
        kitchens: false,
        holidays: true,
        admins: false,
        instantOrders: true,
      };
    case "kitchen-staff":
      return {
        analytics: false,
        orders: true,
        subscriptions: false,
        users: false,
        deliveryAgents: false,
        discounts: false,
        meals: false,
        nowMeals: false,
        swapSets: false,
        kitchens: false,
        holidays: false,
        admins: false,
        instantOrders: true,
      };
    default:
      return {
        analytics: true,
        orders: true,
        subscriptions: true,
        users: true,
        deliveryAgents: true,
        discounts: true,
        meals: true,
        nowMeals: true,
        swapSets: true,
        kitchens: true,
        holidays: true,
        admins: false,
        instantOrders: true,
      };
  }
};

const emptyForm = (): AdminUserPayload => ({
  name: "",
  username: "",
  email: "",
  password: "",
  role: "manager",
  isActive: true,
  appAccessEnabled: false,
  phone: "",
  employeeId: "",
  designation: "",
  department: "",
  panNumber: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  uanNumber: "",
  pfAccountNumber: "",
  esiNumber: "",
  aadharNumber: "",
  dob: "",
  emergencyContactNumber: "",
  emergencyContactRelation: "",
  permissions: buildDefaultPermissions("manager"),
  assignedKitchenIds: [],
});

const UserManagement = () => {
  const [users, setUsers] = useState<Admin[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<Admin | null>(null);
  const [formData, setFormData] = useState<AdminUserPayload>(emptyForm());

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [admins, kitchensResponse] = await Promise.all([
        adminUsersApi.getUsers(),
        kitchensApi.getAdminKitchens(),
      ]);
      setUsers(admins);
      setKitchens(kitchensResponse.kitchens || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load user management data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const kitchenOptions = useMemo(
    () => kitchens.filter((kitchen) => kitchen.isActive),
    [kitchens]
  );

  const openCreate = () => {
    setError("");
    setEditingUser(null);
    setFormData(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (user: Admin) => {
    setError("");
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username || "",
      email: user.email,
      password: "",
      role: user.role,
      isActive: user.isActive !== false,
      appAccessEnabled: user.appAccessEnabled === true,
      phone: user.phone || "",
      employeeId: user.employeeId || "",
      designation: user.designation || "",
      department: user.department || "",
      panNumber: user.panNumber || "",
      bankName: user.bankName || "",
      accountNumber: user.accountNumber || "",
      ifscCode: user.ifscCode || "",
      uanNumber: user.uanNumber || "",
      pfAccountNumber: user.pfAccountNumber || "",
      esiNumber: user.esiNumber || "",
      aadharNumber: user.aadharNumber || "",
      dob: user.dob || "",
      emergencyContactNumber: user.emergencyContactNumber || "",
      emergencyContactRelation: user.emergencyContactRelation || "",
      permissions: { ...user.permissions },
      assignedKitchenIds: user.assignedKitchenIds || [],
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleRoleChange = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      role,
      permissions: buildDefaultPermissions(role),
    }));
  };

  const handlePermissionToggle = (permission: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: checked,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingUser) {
        await adminUsersApi.updateUser(editingUser.id, formData);
      } else {
        await adminUsersApi.createUser(formData);
      }

      closeDialog();
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminUsersApi.deleteUser(id);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to delete user");
    }
  };

  return (
    <Layout>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Staff & User Management
          </Typography>
          <Typography color="text.secondary">
            Manage dashboard staff, payroll details, assigned roles, kitchens, and permissions.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>
          Add Staff Member
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name & ID</TableCell>
              <TableCell>Designation</TableCell>
              <TableCell>System Role</TableCell>
              <TableCell>Email & Phone</TableCell>
              <TableCell>Bank / PAN</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>App access</TableCell>
              <TableCell>Kitchens</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {user.name}
                  </Typography>
                  {user.employeeId && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      ID: {user.employeeId}
                    </Typography>
                  )}
                  {user.username && !user.employeeId && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      @{user.username}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={user.designation ? 600 : 400}>
                    {user.designation || "-"}
                  </Typography>
                  {user.department && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {user.department}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={user.role ? String(user.role).toUpperCase() : "STAFF"}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.email}</Typography>
                  {user.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {user.phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {user.panNumber ? (
                    <Typography variant="caption" display="block" fontWeight={500}>
                      PAN: {user.panNumber}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block">
                      PAN: Not set
                    </Typography>
                  )}
                  {user.bankName && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {user.bankName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={user.isActive === false ? "default" : "success"}
                    label={user.isActive === false ? "Inactive" : "Active"}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={user.appAccessEnabled === true ? "success" : "default"}
                    label={user.appAccessEnabled === true ? "Enabled" : "Disabled"}
                  />
                </TableCell>
                <TableCell>
                  {user.hasGlobalKitchenAccess
                    ? "All"
                    : `${user.assignedKitchenIds?.length || 0} assigned`}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => openEdit(user)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => void handleDelete(user.id)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingUser ? "Edit Staff Member" : "Create Staff Member"}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{error}</Alert>}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Account & Login Info */}
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              1. Account & Login Information
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Full Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label={editingUser ? "New Password (optional)" : "Password *"}
                type="password"
                value={formData.password || ""}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <TextField
                select
                label="System Role"
                value={formData.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                fullWidth
                size="small"
              >
                {ADMIN_ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active Account"
              />
            </Stack>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.appAccessEnabled === true}
                    onChange={(e) => setFormData({ ...formData, appAccessEnabled: e.target.checked })}
                  />
                }
                label="App master access"
              />
              <Typography variant="body2" color="text.secondary">
                Allow sign-in to Kinnow Ops by phone and OTP or password. Requires a unique
                Indian mobile number. Existing role permissions and kitchen assignments still apply.
                Turning this off revokes app sessions and leaves web access unchanged.
              </Typography>
            </Box>

            <Divider />

            {/* Employment & Payroll Info */}
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              2. Employment & Payroll Details (For Payslips)
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Employee ID (e.g. KIN-1042)"
                value={formData.employeeId || ""}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Phone Number"
                helperText="Used for app OTP sign-in when App master access is enabled"
                value={formData.phone || ""}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Designation (e.g. Kitchen Lead, Ops Executive)"
                value={formData.designation || ""}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Department (e.g. Kitchen Operations)"
                value={formData.department || ""}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Employee PAN Number"
                value={formData.panNumber || ""}
                onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Bank Name (e.g. HDFC Bank)"
                value={formData.bankName || ""}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Bank Account Number"
                value={formData.accountNumber || ""}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Bank IFSC Code"
                value={formData.ifscCode || ""}
                onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Date of Birth (DOB)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dob || ""}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Aadhar Number"
                value={formData.aadharNumber || ""}
                onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Emergency Contact Number"
                value={formData.emergencyContactNumber || ""}
                onChange={(e) =>
                  setFormData({ ...formData, emergencyContactNumber: e.target.value })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Emergency Contact Relation (e.g. Father, Spouse, Sibling)"
                value={formData.emergencyContactRelation || ""}
                onChange={(e) =>
                  setFormData({ ...formData, emergencyContactRelation: e.target.value })
                }
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="UAN Number (Optional)"
                value={formData.uanNumber || ""}
                onChange={(e) => setFormData({ ...formData, uanNumber: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="PF Account Number (Optional)"
                value={formData.pfAccountNumber || ""}
                onChange={(e) => setFormData({ ...formData, pfAccountNumber: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="ESI Number (Optional)"
                value={formData.esiNumber || ""}
                onChange={(e) => setFormData({ ...formData, esiNumber: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>

            <Divider />

            {/* Kitchens & Permissions */}
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              3. Kitchen Assignments & Permissions
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="kitchen-assignment-label">Assigned Kitchens</InputLabel>
              <Select
                labelId="kitchen-assignment-label"
                multiple
                value={formData.assignedKitchenIds || []}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    assignedKitchenIds: e.target.value as string[],
                  })
                }
                input={<OutlinedInput label="Assigned Kitchens" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "All kitchens"
                    : kitchenOptions
                        .filter((kitchen) => selected.includes(kitchen._id))
                        .map((kitchen) => kitchen.name)
                        .join(", ")
                }
              >
                {kitchenOptions.map((kitchen) => (
                  <MenuItem key={kitchen._id} value={kitchen._id}>
                    <Checkbox checked={(formData.assignedKitchenIds || []).includes(kitchen._id)} />
                    <ListItemText primary={kitchen.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Feature Access Permissions
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {ADMIN_PERMISSION_KEYS.map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={formData.permissions?.[permission] === true}
                        onChange={(e) =>
                          handlePermissionToggle(permission, e.target.checked)
                        }
                      />
                    }
                    label={permissionLabels[permission]}
                    sx={{
                      minWidth: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 8px)" },
                      m: 0,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={() => void handleSave()} variant="contained" disabled={saving}>
            {editingUser ? "Save Changes" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default UserManagement;
